import { Children, ReactNode } from 'react';

import isEmpty from 'lodash/isEmpty';

import { mdiChevronDown, mdiChevronUp } from '@lumx/icons';
import { Emphasis, Icon, Size, IconButton, IconButtonProps } from '@lumx/react';
import { GenericProps, HasCloseMode } from '@lumx/react/utils/type';
import type { LumxClassName } from '@lumx/core/js/types';
import { classNames } from '@lumx/core/js/utils';
import { useId } from '@lumx/react/hooks/useId';
import { forwardRef } from '@lumx/react/utils/react/forwardRef';
import { RawClickable } from '@lumx/core/js/components/RawClickable';

/**
 * Defines the props of the component.
 */
export interface SideNavigationItemProps extends GenericProps, HasCloseMode {
    /** SideNavigationItem elements. */
    children?: ReactNode;
    /** Emphasis variant. */
    emphasis?: Emphasis;
    /** Label content. */
    label: string | ReactNode;
    /** Icon (SVG path). */
    icon?: string;
    /** Whether the component is open or not. */
    isOpen?: boolean;
    /** Whether the component is selected or not. */
    isSelected?: boolean;
    /**
     * Whether the link points to the current page or not.
     * Sets `aria-current="page"` on the link, which produces the selected style.
     * This does not set the `--is-selected` modifier class: a link component set in `linkAs` can set the
     * attribute by itself, and React never sees it, so the attribute is the only reliable signal.
     */
    isCurrentPage?: boolean;
    /** Custom react component for the link (can be used to inject react router Link). */
    linkAs?: 'a' | any;
    /** Props to pass to the link (minus those already set by the SideNavigationItem props). */
    linkProps?: React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;
    /** Props to pass to the toggle button (minus those already set by the SideNavigationItem props). */
    toggleButtonProps: Pick<IconButtonProps, 'label'> &
        Omit<IconButtonProps, 'label' | 'onClick' | 'icon' | 'emphasis' | 'color' | 'size'>;
    /** On action button click callback. */
    onActionClick?(evt: React.MouseEvent): void;
    /** On click callback. */
    onClick?(evt: React.MouseEvent): void;
}

/**
 * Component display name.
 */
const COMPONENT_NAME = 'SideNavigationItem';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME: LumxClassName<typeof COMPONENT_NAME> = 'lumx-side-navigation-item';
const { block, element } = classNames.bem(CLASSNAME);

/**
 * Component default props.
 */
const DEFAULT_PROPS: Partial<SideNavigationItemProps> = {
    emphasis: Emphasis.high,
    closeMode: 'unmount',
};

/**
 * SideNavigationItem component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
export const SideNavigationItem = forwardRef<SideNavigationItemProps, HTMLLIElement>((props, ref) => {
    const {
        children,
        className,
        emphasis = DEFAULT_PROPS.emphasis,
        icon,
        isOpen,
        isSelected,
        isCurrentPage,
        label,
        linkAs,
        linkProps,
        onActionClick,
        onClick,
        toggleButtonProps,
        closeMode = DEFAULT_PROPS.closeMode,
        ...forwardedProps
    } = props;

    const hasContent = !isEmpty(Children.toArray(children));
    const shouldSplitActions = Boolean(onActionClick);
    const showChildren = hasContent && isOpen;

    // Mark the link as the current page. A link component set in `linkAs` can also set it by itself.
    const ariaCurrent = isCurrentPage ? 'page' : undefined;

    const contentId = useId();
    const ariaProps: any = {};
    if (hasContent) {
        ariaProps['aria-expanded'] = !!showChildren;
        // Associate with content ID only if in DOM (shown or hidden and not unmounted)
        ariaProps['aria-controls'] = showChildren || closeMode === 'hide' ? contentId : undefined;
    }

    return (
        <li
            ref={ref}
            {...forwardedProps}
            className={classNames.join(
                className,
                block({
                    [`emphasis-${emphasis}`]: Boolean(emphasis),
                    'is-open': showChildren,
                    'is-selected': isSelected,
                }),
            )}
        >
            {shouldSplitActions ? (
                <div className={element('wrapper')}>
                    {RawClickable({
                        as: linkAs || (linkProps?.href ? 'a' : 'button'),
                        'aria-current': ariaCurrent,
                        ...(linkProps as any),
                        className: element('link'),
                        handleClick: onClick,
                        handleKeyPress: linkProps?.onKeyPress,
                        children: (
                            <>
                                {icon && <Icon className={element('icon')} icon={icon} size={Size.xs} />}
                                <span>{label}</span>
                            </>
                        ),
                    })}

                    <IconButton
                        {...toggleButtonProps}
                        className={element('toggle')}
                        icon={isOpen ? mdiChevronUp : mdiChevronDown}
                        size={Size.m}
                        emphasis={Emphasis.low}
                        onClick={onActionClick}
                        {...ariaProps}
                    />
                </div>
            ) : (
                RawClickable({
                    as: linkAs || (linkProps?.href ? 'a' : 'button'),
                    'aria-current': ariaCurrent,
                    ...linkProps,
                    className: element('link'),
                    handleClick: onClick,
                    handleKeyPress: linkProps?.onKeyPress,
                    ...ariaProps,
                    children: (
                        <>
                            {icon && <Icon className={element('icon')} icon={icon} size={Size.xs} />}
                            <span>{label}</span>
                            {hasContent && (
                                <Icon
                                    className={element('chevron')}
                                    icon={isOpen ? mdiChevronUp : mdiChevronDown}
                                    size={Size.xs}
                                />
                            )}
                        </>
                    ),
                })
            )}

            {(closeMode === 'hide' || showChildren) && (
                <ul className={element('children')} id={contentId}>
                    {children}
                </ul>
            )}
        </li>
    );
});
SideNavigationItem.displayName = COMPONENT_NAME;
SideNavigationItem.className = CLASSNAME;
SideNavigationItem.defaultProps = DEFAULT_PROPS;
